<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add New Member</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Household Info -->
          <div v-if="household" class="alert alert-info mb-4">
            <h6 class="alert-heading">{{ household.household_number }}</h6>
            <p class="mb-0">
              Head: {{ household.household_head_name || 'Not assigned' }}<br>
              Current Members: {{ household.member_count || 0 }}
            </p>
          </div>

          <!-- Duplicate Check Alert -->
          <div v-if="possibleDuplicates.length > 0 && step === 1" class="alert alert-warning mb-3">
            <h6 class="alert-heading">Possible Duplicate Found!</h6>
            <p class="mb-2">A member with similar details already exists in the system:</p>
            <div v-for="duplicate in possibleDuplicates" :key="duplicate.member_id" class="mb-2">
              <strong>{{ duplicate.first_name }} {{ duplicate.surname }}</strong><br>
              <small>
                DOB: {{ duplicate.date_of_birth }} |
                Current Household: {{ duplicate.household_number || 'None' }} |
                Location: {{ duplicate.kebele_name }}, {{ duplicate.woreda_name }}
              </small>
            </div>
            <hr>
            <p class="mb-0">
              <small>If you continue, the system will add this existing member to the current household.</small>
            </p>
          </div>

          <!-- Step 1: Basic Information -->
          <div v-if="step === 1">
            <h6 class="mb-3">Basic Information</h6>

            <div class="row">
              <!-- Name Fields -->
              <div class="col-md-6 mb-3">
                <label class="form-label">First Name <span class="text-danger">*</span></label>
                <input v-model="memberForm.firstName" type="text" class="form-control"
                       :class="{'is-invalid': errors.firstName}" required>
                <div v-if="errors.firstName" class="invalid-feedback">{{ errors.firstName }}</div>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Middle Name</label>
                <input v-model="memberForm.middleName" type="text" class="form-control">
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Surname <span class="text-danger">*</span></label>
                <input v-model="memberForm.surname" type="text" class="form-control"
                       :class="{'is-invalid': errors.surname}" required>
                <div v-if="errors.surname" class="invalid-feedback">{{ errors.surname }}</div>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Grandfather Name</label>
                <input v-model="memberForm.grandfatherName" type="text" class="form-control">
              </div>

              <!-- Gender and DOB -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Gender <span class="text-danger">*</span></label>
                <select v-model="memberForm.gender" class="form-select"
                        :class="{'is-invalid': errors.gender}" required>
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
                <div v-if="errors.gender" class="invalid-feedback">{{ errors.gender }}</div>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Date of Birth <span class="text-danger">*</span></label>
                <input v-model="memberForm.dateOfBirth" type="date" class="form-control"
                       :class="{'is-invalid': errors.dateOfBirth}"
                       :max="today" required
                       @change="checkForDuplicates">
                <div v-if="errors.dateOfBirth" class="invalid-feedback">{{ errors.dateOfBirth }}</div>
                <small v-if="memberForm.dateOfBirth" class="text-muted">
                  Age: {{ calculateAge(memberForm.dateOfBirth) }}
                </small>
              </div>

              <!-- Relationship -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Relationship to Head <span class="text-danger">*</span></label>
                <select v-model="memberForm.relationshipToHead" class="form-select"
                        :class="{'is-invalid': errors.relationshipToHead}" required>
                  <option value="">Select Relationship</option>
                  <option value="head">Head of Household</option>
                  <option value="spouse">Spouse</option>
                  <option value="child">Child</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="grandchild">Grandchild</option>
                  <option value="grandparent">Grandparent</option>
                  <option value="other_relative">Other Relative</option>
                  <option value="non_relative">Non-Relative</option>
                </select>
                <div v-if="errors.relationshipToHead" class="invalid-feedback">{{ errors.relationshipToHead }}</div>
              </div>

              <!-- Marital Status -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Marital Status</label>
                <select v-model="memberForm.maritalStatus" class="form-select">
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                  <option value="separated">Separated</option>
                </select>
              </div>

              <!-- Contact Information -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Phone Number</label>
                <input v-model="memberForm.phoneNumber" type="tel" class="form-control"
                       placeholder="+251...">
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">National ID</label>
                <input v-model="memberForm.nationalId" type="text" class="form-control">
              </div>

              <!-- Occupation and Income -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Occupation</label>
                <input v-model="memberForm.occupation" type="text" class="form-control">
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Monthly Income (ETB)</label>
                <input v-model="memberForm.monthlyIncome" type="number" class="form-control" min="0">
              </div>
            </div>

            <!-- Special Categories -->
            <div class="row">
              <div class="col-12 mb-3">
                <label class="form-label">Special Categories</label>
                <div class="d-flex flex-wrap gap-3">
                  <div class="form-check">
                    <input v-model="memberForm.isPregnant" class="form-check-input"
                           type="checkbox" id="pregnantCheck"
                           :disabled="memberForm.gender !== 'female' || !isReproductiveAge()">
                    <label class="form-check-label" for="pregnantCheck">
                      Currently Pregnant
                    </label>
                  </div>
                  <div class="form-check">
                    <input v-model="memberForm.hasDisability" class="form-check-input"
                           type="checkbox" id="disabilityCheck">
                    <label class="form-check-label" for="disabilityCheck">
                      Has Disability
                    </label>
                  </div>
                  <div class="form-check">
                    <input v-model="memberForm.hasChronicCondition" class="form-check-input"
                           type="checkbox" id="chronicCheck">
                    <label class="form-check-label" for="chronicCheck">
                      Has Chronic Condition
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: CBHI & Health Information -->
          <div v-else-if="step === 2">
            <h6 class="mb-3">CBHI & Health Information</h6>

            <!-- CBHI Enrollment -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">CBHI Enrollment Status <span class="text-danger">*</span></label>
                <select v-model="memberForm.enrollmentType" class="form-select" required>
                  <option value="cbhi_member">CBHI Member</option>
                  <option value="community_member">Non-CBHI (Community Member)</option>
                </select>
              </div>

              <div v-if="memberForm.enrollmentType === 'cbhi_member'" class="col-md-6 mb-3">
                <label class="form-label">CBHI Number</label>
                <input v-model="memberForm.cbhiNumber" type="text" class="form-control"
                       placeholder="CBHI membership number">
              </div>
            </div>

            <!-- Health Profile -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Blood Group</label>
                <select v-model="healthProfile.bloodGroup" class="form-select">
                  <option value="">Unknown</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Vaccination Status</label>
                <select v-model="healthProfile.vaccinationStatus" class="form-select">
                  <option value="unknown">Unknown</option>
                  <option value="up_to_date">Up to Date</option>
                  <option value="partial">Partial</option>
                  <option value="not_vaccinated">Not Vaccinated</option>
                </select>
              </div>
            </div>

            <!-- Disabilities (if checked) -->
            <div v-if="memberForm.hasDisability" class="mb-3">
              <label class="form-label">Disability Details</label>
              <textarea v-model="healthProfile.disabilities" class="form-control" rows="2"
                        placeholder="Describe disabilities..."></textarea>
            </div>

            <!-- Chronic Conditions (if checked) -->
            <div v-if="memberForm.hasChronicCondition" class="mb-3">
              <label class="form-label">Chronic Conditions</label>
              <div class="row g-2 mb-2">
                <div v-for="condition in commonConditions" :key="condition" class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox"
                           :id="'condition_' + condition"
                           :value="condition" v-model="selectedConditions">
                    <label class="form-check-label" :for="'condition_' + condition">
                      {{ condition }}
                    </label>
                  </div>
                </div>
              </div>
              <input v-model="otherConditions" type="text" class="form-control mt-2"
                     placeholder="Other conditions (comma separated)">
            </div>

            <!-- Allergies -->
            <div class="mb-3">
              <label class="form-label">Known Allergies</label>
              <textarea v-model="healthProfile.allergies" class="form-control" rows="2"
                        placeholder="List any known allergies..."></textarea>
            </div>

            <!-- Current Medications -->
            <div class="mb-3">
              <label class="form-label">Current Medications</label>
              <textarea v-model="healthProfile.currentMedications" class="form-control" rows="2"
                        placeholder="List current medications..."></textarea>
            </div>
          </div>

          <!-- Step 3: Pregnancy Details (if applicable) -->
          <div v-else-if="step === 3 && memberForm.isPregnant">
            <h6 class="mb-3">Pregnancy Information</h6>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Last Menstrual Period (LMP) <span class="text-danger">*</span></label>
                <input v-model="pregnancyForm.lmpDate" type="date" class="form-control"
                       :max="today" required>
                <small v-if="pregnancyForm.lmpDate" class="text-muted">
                  EDD: {{ calculateEDD(pregnancyForm.lmpDate) }}
                </small>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Gravida <span class="text-danger">*</span></label>
                <input v-model="pregnancyForm.gravida" type="number" class="form-control"
                       min="1" required>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Para</label>
                <input v-model="pregnancyForm.para" type="number" class="form-control"
                       min="0" :max="pregnancyForm.gravida - 1">
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Living Children</label>
                <input v-model="pregnancyForm.livingChildren" type="number" class="form-control"
                       min="0" :max="pregnancyForm.para">
              </div>

              <div class="col-12 mb-3">
                <label class="form-label">Risk Factors</label>
                <div class="row g-2">
                  <div v-for="risk in pregnancyRiskFactors" :key="risk" class="col-md-6">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox"
                             :id="'risk_' + risk"
                             :value="risk" v-model="selectedRiskFactors">
                      <label class="form-check-label" :for="'risk_' + risk">
                        {{ risk }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Planned Delivery Place</label>
                <select v-model="pregnancyForm.deliveryPlan" class="form-select">
                  <option value="health_center">Health Center</option>
                  <option value="hospital">Hospital</option>
                  <option value="health_post">Health Post</option>
                  <option value="home">Home</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="validateAndNext" :disabled="loading">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 2">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button v-if="memberForm.isPregnant" type="button" class="btn btn-primary"
                    @click="nextStep" :disabled="loading">
              Next <i class="bx bx-chevron-right"></i>
            </button>
            <button v-else type="button" class="btn btn-success" @click="saveMember"
                    :disabled="submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Saving...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Save Member
              </span>
            </button>
          </div>

          <div v-else-if="step === 3">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-success" @click="saveMember"
                    :disabled="!pregnancyForm.lmpDate || submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Saving...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Save Member
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "AddMemberModal",
  props: {
    household: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,
      checkingDuplicate: false,
      possibleDuplicates: [],

      // Form data
      memberForm: {
        firstName: '',
        middleName: '',
        surname: '',
        grandfatherName: '',
        gender: '',
        dateOfBirth: '',
        relationshipToHead: '',
        maritalStatus: 'single',
        phoneNumber: '',
        nationalId: '',
        occupation: '',
        monthlyIncome: 0,
        enrollmentType: 'cbhi_member',
        cbhiNumber: '',
        isPregnant: false,
        hasDisability: false,
        hasChronicCondition: false
      },

      healthProfile: {
        bloodGroup: '',
        vaccinationStatus: 'unknown',
        disabilities: '',
        allergies: '',
        currentMedications: ''
      },

      pregnancyForm: {
        lmpDate: '',
        gravida: 1,
        para: 0,
        livingChildren: 0,
        deliveryPlan: 'health_center'
      },

      // Validation errors
      errors: {},

      // Options
      commonConditions: [
        'Hypertension',
        'Diabetes',
        'HIV/AIDS',
        'Tuberculosis',
        'Asthma',
        'Epilepsy'
      ],
      pregnancyRiskFactors: [
        'Age < 18 or > 35',
        'Previous C-section',
        'Multiple pregnancy',
        'Hypertension',
        'Diabetes',
        'Anemia',
        'Previous stillbirth',
        'Previous hemorrhage'
      ],

      // Selected items
      selectedConditions: [],
      otherConditions: '',
      selectedRiskFactors: [],

      today: new Date().toISOString().split('T')[0]
    };
  },

  methods: {
    async checkForDuplicates() {
      if (!this.memberForm.firstName || !this.memberForm.surname || !this.memberForm.dateOfBirth) {
        return;
      }

      this.checkingDuplicate = true;

      const data = {
        "function": "checkMemberExists",
        "firstName": this.memberForm.firstName,
        "surname": this.memberForm.surname,
        "dateOfBirth": this.memberForm.dateOfBirth,
        "nationalId": this.memberForm.nationalId,
        "phoneNumber": this.memberForm.phoneNumber,
        "cbhiNumber": this.memberForm.cbhiNumber
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success && res.data.data.exists) {
          this.possibleDuplicates = res.data.data.members;
        } else {
          this.possibleDuplicates = [];
        }
      } catch (e) {
        console.error("Failed to check for duplicates:", e);
      } finally {
        this.checkingDuplicate = false;
      }
    },

    validateAndNext() {
      this.errors = {};

      // Validate required fields
      if (!this.memberForm.firstName.trim()) {
        this.errors.firstName = 'First name is required';
      }
      if (!this.memberForm.surname.trim()) {
        this.errors.surname = 'Surname is required';
      }
      if (!this.memberForm.gender) {
        this.errors.gender = 'Gender is required';
      }
      if (!this.memberForm.dateOfBirth) {
        this.errors.dateOfBirth = 'Date of birth is required';
      }
      if (!this.memberForm.relationshipToHead) {
        this.errors.relationshipToHead = 'Relationship is required';
      }

      if (Object.keys(this.errors).length === 0) {
        this.step = 2;
      }
    },

    nextStep() {
      if (this.step === 2 && this.memberForm.isPregnant) {
        this.step = 3;
      }
    },

    previousStep() {
      this.step--;
    },

    async saveMember() {
      this.submitting = true;

      // Prepare chronic conditions
      let chronicConditions = [...this.selectedConditions];
      if (this.otherConditions) {
        chronicConditions = chronicConditions.concat(
            this.otherConditions.split(',').map(c => c.trim()).filter(c => c)
        );
      }

      const data = {
        "function": "registerOrAddMember",
        "householdId": this.household.household_id,
        "firstName": this.memberForm.firstName,
        "middleName": this.memberForm.middleName,
        "surname": this.memberForm.surname,
        "grandfatherName": this.memberForm.grandfatherName,
        "gender": this.memberForm.gender,
        "dateOfBirth": this.memberForm.dateOfBirth,
        "relationshipToHead": this.memberForm.relationshipToHead,
        "maritalStatus": this.memberForm.maritalStatus,
        "phoneNumber": this.memberForm.phoneNumber,
        "nationalId": this.memberForm.nationalId,
        "occupation": this.memberForm.occupation,
        "monthlyIncome": this.memberForm.monthlyIncome || 0,
        "enrollmentType": this.memberForm.enrollmentType,
        "cbhiNumber": this.memberForm.cbhiNumber,
        "healthProfile": {
          "bloodGroup": this.healthProfile.bloodGroup,
          "vaccinationStatus": this.healthProfile.vaccinationStatus,
          "chronicConditions": chronicConditions.join(', '),
          "disabilities": this.healthProfile.disabilities,
          "allergies": this.healthProfile.allergies,
          "currentMedications": this.healthProfile.currentMedications
        }
      };

      // Add pregnancy data if applicable
      if (this.memberForm.isPregnant) {
        data.pregnancy = {
          "lmpDate": this.pregnancyForm.lmpDate,
          "gravida": this.pregnancyForm.gravida,
          "para": this.pregnancyForm.para,
          "livingChildren": this.pregnancyForm.livingChildren,
          "riskFactors": this.selectedRiskFactors.join(', '),
          "highRisk": this.selectedRiskFactors.length > 0,
          "deliveryPlan": this.pregnancyForm.deliveryPlan
        };
      }

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          const result = res.data.data;

          // Show appropriate message based on action
          if (result.action === 'already_in_household') {
            alert('This member is already in this household.');
          } else if (result.action === 'added_existing') {
            if (confirm(`This member already exists in the system and was in household ${result.previousHousehold}. They have been moved to this household. Continue?`)) {
              this.$emit('added', result);
            }
          } else {
            // New member created
            this.$emit('added', result);
          }
        } else {
          alert(res.data.message || 'Failed to add member');
        }
      } catch (e) {
        console.error("Failed to add member:", e);
        console.error("Response:", e.response);
        alert('Failed to add member: ' + (e.response?.data?.message || e.message || 'Unknown error'));
      } finally {
        this.submitting = false;
      }
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return '';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const ageYears = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return ageYears - 1 + ' years';
      }

      return ageYears + ' years';
    },

    isReproductiveAge() {
      if (!this.memberForm.dateOfBirth) return false;

      const birthDate = new Date(this.memberForm.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();

      return age >= 15 && age <= 49;
    },

    calculateEDD(lmpDate) {
      if (!lmpDate) return '';

      const lmp = new Date(lmpDate);
      const edd = new Date(lmp.getTime() + (280 * 24 * 60 * 60 * 1000)); // Add 280 days

      return edd.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.form-check-input:disabled + .form-check-label {
  opacity: 0.5;
}

.invalid-feedback {
  display: block;
}
</style>